/**
 * Wikipedia-style wiki URLs.
 *
 * The article's own TITLE is the URL, with spaces as underscores, and
 * categories live in a `Category:` namespace — exactly how en.wikipedia.org
 * addresses its pages:
 *
 *   /wiki/Durham_County_Courthouse
 *   /wiki/Category:Counties
 *
 * The legacy lowercase-hyphen slugs (`/wiki/durham-county-courthouse`,
 * `/wiki/category/counties`) still resolve — the backend accepts all three
 * shapes and the page redirects to the canonical one — so every link that is
 * already out in the world (and its ranking) keeps working.
 *
 * `slug` is still the storage key in Mongo. Nothing here changes the database;
 * this module is purely about what the browser and crawlers see.
 */

export const CATEGORY_PREFIX = "Category:";
export const SITE_URL = "https://kinglawnc.com";

/** The five curated categories, by their legacy URL segment. */
export const LEGACY_CATEGORY_NAMES = {
  laws: "Laws",
  "how-to": "How To",
  counties: "Counties",
  judges: "Judges",
  scenarios: "Scenarios",
};

function decode(segment) {
  try {
    return decodeURIComponent(String(segment ?? ""));
  } catch {
    return String(segment ?? "");
  }
}

/**
 * Title → URL segment. Spaces become underscores; `:` and `,` stay literal
 * (Wikipedia keeps them readable), everything else is percent-encoded so a
 * title with `?`, `#` or `/` in it can't break the route.
 */
export function titleToWikiSegment(title) {
  const underscored = String(title ?? "").trim().replace(/\s+/g, "_");
  return encodeURIComponent(underscored)
    .replace(/%3A/gi, ":")
    .replace(/%2C/gi, ",");
}

/** URL segment → title. Underscores become spaces. */
export function wikiSegmentToTitle(segment) {
  return decode(segment).replace(/_/g, " ").replace(/\s+/g, " ").trim();
}

/** True when this `/wiki/<segment>` addresses the Category namespace. */
export function isWikiCategorySegment(segment) {
  return /^category:/i.test(decode(segment).trim());
}

/** `Category:How_To` → `How To`. */
export function categoryNameFromSegment(segment) {
  const raw = decode(segment).trim();
  const withoutPrefix = /^category:/i.test(raw)
    ? raw.slice(CATEGORY_PREFIX.length)
    : raw;
  const name = withoutPrefix.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  // A legacy segment (`how-to`) still has to name a real category.
  return LEGACY_CATEGORY_NAMES[name.toLowerCase()] || name;
}

/** Canonical path for an article. Accepts a page object or a bare title. */
export function wikiPagePath(pageOrTitle) {
  const page = typeof pageOrTitle === "string" ? { title: pageOrTitle } : (pageOrTitle || {});
  const title = String(page.title ?? "").trim();
  if (title) return `/wiki/${titleToWikiSegment(title)}`;
  // Title-less orphan rows can still be addressed by their stored slug.
  return page.slug ? `/wiki/${encodeURIComponent(page.slug)}` : "/wiki";
}

/** Canonical path for a category listing. */
export function wikiCategoryPath(categoryOrSegment) {
  const name =
    LEGACY_CATEGORY_NAMES[String(categoryOrSegment ?? "").toLowerCase()] ||
    String(categoryOrSegment ?? "").trim();
  if (!name) return "/wiki";
  return `/wiki/${CATEGORY_PREFIX}${titleToWikiSegment(name)}`;
}

export function wikiPageUrl(pageOrTitle) {
  return `${SITE_URL}${wikiPagePath(pageOrTitle)}`;
}

export function wikiCategoryUrl(categoryOrSegment) {
  return `${SITE_URL}${wikiCategoryPath(categoryOrSegment)}`;
}

/**
 * Is the address in the URL bar already the page's canonical form? Compared
 * decoded, so `Child Support` (a pasted space) and `child-support` (the legacy
 * slug) both fail and redirect to `Child_Support`, while the canonical form
 * itself compares equal and can't loop.
 */
export function isCanonicalWikiAddress(address, title) {
  return decode(address) === decode(titleToWikiSegment(title));
}

/**
 * Do two address forms point at the same page? The loose comparison the
 * backend resolver uses — case, underscores and the legacy hyphen slug all
 * compare equal.
 */
export function sameWikiTarget(a, b) {
  const key = (s) =>
    wikiSegmentToTitle(s)
      .toLowerCase()
      .replace(/[-\s]+/g, " ")
      .trim();
  return key(a) === key(b);
}
